import { chunk } from "lodash";
import AppContext from "PFApp/app_context";
import LazyLocalExportTrigger from "PFApp/components/document_buttons/lazy_local_export_trigger";
import FiltersBox from "PFApp/components/filters/filters_box/filters_box";
import FiltersButton from "PFApp/components/filters/filters_button";
import FiltersPreview from "PFApp/components/filters/filters_preview";
import { SearchInput } from "PFApp/components/search_input";
import { Typography } from "PFComponents/typography";
import { getTermFilterData } from "PFCore/helpers/get_term_filter_data";
import useIsPermittedTo from "PFCore/helpers/use_is_permitted_to";
import { getExportProfiles } from "PFCore/services/export_profiles";
import { PermissionRule } from "PFTypes";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { useToggle } from "react-use";

import { useProfilesPageContext } from "../../profiles_page_context";
import css from "./profiles_header.module.scss";

export const ProfilesHeader = () => {
  const { t } = useTranslation("profiles");
  const {
    store: { currentProfile }
  } = useContext(AppContext);
  const { profiles, meta, isFetching, updateFilter, resetFilters, areFiltersClear } =
    useProfilesPageContext();
  const { termValue, termFilter } = getTermFilterData(meta);
  const [filtersOpen, toggleFiltersOpen] = useToggle(false);

  const canExportDocs = useIsPermittedTo(currentProfile)(PermissionRule.ExportDocuments);

  return (
    <div>
      <Typography variant="h1">{t("list.profiles")}</Typography>
      <div className={css.actionsRow}>
        <SearchInput
          term={termValue}
          onTermChange={(newTerm: string) => {
            termFilter && updateFilter(termFilter, newTerm);
          }}
        />
        {!areFiltersClear && (
          <div className={css.preview}>
            <FiltersPreview
              meta={meta}
              filtersAreClear={areFiltersClear}
              clearFilters={() => resetFilters({})}
              onFilterChange={updateFilter}
              onMoreClicked={() => toggleFiltersOpen(true)}
            />
          </div>
        )}
        <span className={css.actionButtons}>
          <FiltersButton filtersAreClear={areFiltersClear} onClick={toggleFiltersOpen} />
          {canExportDocs && profiles && (
            <LazyLocalExportTrigger
              buttonKind="secondary"
              label=""
              icon="export"
              quantity={profiles.length}
              getProfilesCollection={(paginationPage = 1, paginationSize = 50) => {
                const items = profiles.map(({ profile }) => profile.id);

                const paginatedArray = chunk(items, paginationSize);

                return getExportProfiles({
                  profileIds: paginatedArray[paginationPage - 1]
                }).then((resp) => ({
                  entries: resp.map((profile) => ({
                    profile
                  })),
                  meta: { total: items.length, page: paginationPage }
                }));
              }}
            />
          )}
        </span>
      </div>
      {filtersOpen && (
        <div className={css.filtersBoxWrapper}>
          <FiltersBox
            meta={meta}
            isLoading={isFetching}
            closeFilters={toggleFiltersOpen}
            filtersAreClear={areFiltersClear}
            clearFilters={resetFilters}
            handleFilterChange={updateFilter}
            classes={{ root: css.filtersBox }}
          />
        </div>
      )}
    </div>
  );
};
